import type {ReactElement} from 'react';
import React from 'react';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import {ContextualMenuMediaControlOptions} from '@Components/contextual-menu/components/contextual-menu-media-control-options';
import type {MediaControlsItemProps} from '@Components/contextual-menu/components/media-controls-item';
import {onOpenCropModal, onOpenMaskingModal, onOpenReplaceMedia} from '@Components/poster-editor/library/poster-editor-open-modals';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import {IS_AI_EDIT_IMAGE_GRAPHIC_ITEM_ENABLED, removeImageBackgroundFromId, removeImageSlideBackgroundFromId} from '@Components/poster-editor/library/poster-editor-library';
import type {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import {ReplaceMediaControls} from '@Components/replace-media-controls';
import {RemoveImageBackgroundNotificationTag} from '@Components/poster-editor/components/remove-image-background-notification-tag';
import {doesSupportRemoveBackground} from '@Libraries/image-item.library';
import styles from './base-image-media-controls.module.scss';
import {GA4EventName, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {RemoveBackgroundStatus} from '@PosterWhiteboard/items/item/item.types';
import {isUserPremium} from '@Libraries/user.library';

interface BaseImageMediaControlsProps {
  activeItem: ImageItem | ImageSlideItem;
  removeBackgroundStatus: RemoveBackgroundStatus;

  onReplaceImage(itemData: ItemData): void;

  onClickAiEdit: () => void;
}

enum ImageMediaControlsOptions {
  CROP = 'crop',
  REPLACE = 'replace',
  REMOVE_BG = 'remove-bg',
  MASK = 'mask',
  AI_EDIT = 'ai-edit',
}

export function BaseImageMediaControls({...props}: BaseImageMediaControlsProps): ReactElement | null {
  if (props.activeItem.isDrawing()) {
    return null;
  }

  if (!props.activeItem || (props.activeItem && !('cropData' in props.activeItem))) {
    return null;
  }

  const onClickCrop = (): void => {
    onOpenCropModal(props.activeItem);
  };

  const onClickRemoveBG = async (): Promise<void> => {
    if (props.removeBackgroundStatus === RemoveBackgroundStatus.REMOVED) {
      await props.activeItem.disableRemoveBackgroundFlag();
      return;
    }

    if (props.activeItem.isImageSlide()) {
      removeImageSlideBackgroundFromId(props.activeItem.slideshow.uid, props.activeItem.uid);
    } else {
      removeImageBackgroundFromId(props.activeItem.uid);
    }
    trackPosterBuilderGA4Events(GA4EventName.REMOVE_BACKGROUND, {[GA4EventParamName.SOURCE]: GA4EventSources.CONTEXTUAL_MENU});
  };

  const onClickMask = async (): Promise<void> => {
    return onOpenMaskingModal(props.activeItem);
  };

  const onReplaceCallback = (id: ReplaceMediaControlsButtons): void => {
    onOpenReplaceMedia(id, props.activeItem, (itemData: ItemData): void => {
      props.onReplaceImage(itemData);
    });
  };

  const getItems = (): MediaControlsItemProps[] => {
    const items: MediaControlsItemProps[] = [];

    if (doesSupportRemoveBackground(props.activeItem.imageSource)) {
      items.push({
        id: `${ImageMediaControlsOptions.REMOVE_BG}-media-control`,
        icon: 'icon-remove-bg',
        text: window.i18next.t('pmwjs_remove_bg'),
        iconClasses: 'content-ai',
        textColorClasses: 'content-ai',
        iconContainerClasses: props.removeBackgroundStatus === RemoveBackgroundStatus.REMOVED ? styles.AIBG : '',
        isLoading: props.removeBackgroundStatus === RemoveBackgroundStatus.REMOVING || (props.activeItem.isImageUploading() && isUserPremium()),
        loadingText: props.activeItem.isImageUploading() ? window.i18next.t('pmwjs_please_wait') : window.i18next.t('pmwjs_removing_background'),
        isSelected: props.removeBackgroundStatus === RemoveBackgroundStatus.REMOVED,
        tag: <RemoveImageBackgroundNotificationTag hashedFilename={props.activeItem.hashedFilename} imageSource={props.activeItem.imageSource} />,
        onClick: (): void => {
          void onClickRemoveBG();
        },
        textPaddingClasses: 'spacing-p-r-1 spacing-p-l-1',
        tagContainerClassName: '_full-width',
      });
    }

    items.push(
      {
        id: `${ImageMediaControlsOptions.CROP}-media-control`,
        icon: 'icon-crop',
        text: window.i18next.t('pmwjs_crop'),
        onClick: onClickCrop,
      },
      {
        id: `${ImageMediaControlsOptions.MASK}-media-control`,
        icon: 'icon-mask',
        text: window.i18next.t('pmwjs_mask'),
        isSelected: props.activeItem.isMasked(),
        onClick: (): void => {
          void onClickMask();
        },
      },
      {
        id: `${ImageMediaControlsOptions.REPLACE}-media-control`,
        icon: 'icon-replace',
        text: window.i18next.t('pmwjs_replace'),
        dropdownContent: (
          <ReplaceMediaControls
            onClick={(id: ReplaceMediaControlsButtons): void => {
              onReplaceCallback(id);
            }}
          />
        ),
      }
    );

    if (IS_AI_EDIT_IMAGE_GRAPHIC_ITEM_ENABLED) {
      items.push({
        id: `${ImageMediaControlsOptions.AI_EDIT}-media-control`,
        icon: 'icon-sparkle',
        text: window.i18next.t('pmwjs_ai_edit'),
        onClick: (): void => {
          props.onClickAiEdit();
        },
        textPaddingClasses: 'spacing-p-r-4 spacing-p-l-4',
        tagContainerClassName: '_full-width',
      });
    }

    return items;
  };
  return <ContextualMenuMediaControlOptions id="image-media-controls" items={getItems()} />;
}
